import { gql } from 'graphql-tag';
import { v4 as uuid } from 'uuid';
import { useMutation } from '@apollo/client';
import { useState, useCallback } from 'react';
import { useMeContext } from '~/modules/me';
import resourceSkillsFragment from '~/modules/resource-management/graphql/resourceSkills.fragment';
import useApplyUserDefinedSkillModificationsForUser from './useApplyUserDefinedSkillModificationsForUser';

const UPDATE_SKILLS = gql`
  mutation updateUserSkills($input: UpdateUserSkillsInput2!) {
    updateUserSkills: updateUserSkills3(input: $input) {
      skills {
        category {
          id
          uri
          displayText
        }
        skillAssignments {
          id
          uri
          displayText
          certificate
          skillLevel {
            id
            uri
            displayText
            rating
          }
          skillCustomMetadata {
            uri
            type
            text
            date
            number
            displayText
          }
        }
      }
    }
  }
`;

export const useUpdateSkillsMutation = () => useMutation(UPDATE_SKILLS);

const updateResourceSkillsCache = ({ userUri, proxy, mutationResponse }) => {
  const {
    data: {
      updateUserSkills: { skills: updatedSkills }
    }
  } = mutationResponse;

  const cachedFragmentId = `ResourceDetails:${userUri}`;

  const cachedResourceDetails = proxy.readFragment({
    id: cachedFragmentId,
    fragment: resourceSkillsFragment,
    fragmentName: 'ResourceSkillsFragment'
  });

  proxy.writeFragment({
    id: cachedFragmentId,
    fragment: resourceSkillsFragment,
    fragmentName: 'ResourceSkillsFragment',
    data: {
      ...cachedResourceDetails,
      resourceSkill: updatedSkills
    }
  });
};

export const updateCache = userUri => (proxy, mutationResponse) => {
  updateResourceSkillsCache({ userUri, proxy, mutationResponse });
};

const isLocalObject = obj => obj.isNew || obj.id.startsWith('LOCAL');

const mapCustomMetaData = skill =>
  skill.customMetadata.map(cm => ({
    uri: cm.uri,
    type: cm.type,
    text: cm.text,
    date: cm.date,
    number: cm.number,
    displayText: cm.displayText
  }));

const mapCacheCustomMetaData = skill =>
  skill.customMetadata.map(meta => ({
    __typename: 'SkillCustomMetadata',
    ...meta
  }));

const mapUpdatedUserDefinedSkills = ({ displayText, skillLevel, id }) => ({
  id,
  name: displayText,
  skillLevelId: skillLevel?.id
});

const mapAddUserDefinedSkills = ({ displayText, skillLevel }) => ({
  skillName: displayText,
  skillLevelId: skillLevel?.id
});

export const useUpdateUserSkillAssignments = ({ userUri, initialValues }) => {
  const {
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  } = useMeContext();
  const [updateSkillsMutation] = useUpdateSkillsMutation();
  const {
    applyUserDefinedSkillModificationsForUser
  } = useApplyUserDefinedSkillModificationsForUser({ userId: userUri });

  const [error, setError] = useState();

  const onSubmit = useCallback(
    async values => {
      const { skills, userDefinedSkills = [] } = values;

      const skillAssignments = skills.map(skill => {
        return {
          skillUri: isLocalObject(skill) ? null : skill.uri,
          skillLevelUri: skill.skillLevel ? skill.skillLevel.uri : null,
          certificate: skill.certificate,
          skillCustomMetadata: mapCustomMetaData(skill),
          name: (skill.displayText || skill.name).trim(),
          category: {
            id: isLocalObject(skill.category) ? null : skill.category.id,
            name: isLocalObject(skill.category)
              ? (skill.category.displayText || skill.category.name).trim()
              : undefined
          }
        };
      });

      const updateSkillsMutationParams = {
        variables: {
          input: { userUri, skills: skillAssignments }
        },
        update: updateCache(userUri),
        optimisticResponse: {
          __typename: 'Mutation',
          updateUserSkills: {
            __typename: 'updateSkillsResult',
            skills: Object.values(
              skills.reduce((acc, skill) => {
                const categoryId = skill.category.id;

                if (!acc[categoryId]) {
                  const categoryUri = uuid();

                  acc[categoryId] = {
                    __typename: 'ResourceSkillDetails',
                    category: isLocalObject(skill.category)
                      ? {
                          __typename: 'Category',
                          id: categoryUri,
                          uri: categoryUri,
                          name:
                            skill.category.displayText || skill.category.name,
                          displayText:
                            skill.category.displayText || skill.category.name
                        }
                      : skill.category,
                    skillAssignments: []
                  };
                }

                const skillUri = isLocalObject(skill) ? uuid() : skill.id;

                acc[categoryId].skillAssignments.push({
                  __typename: 'Skill',
                  id: skillUri,
                  uri: skillUri,
                  displayText: skill.displayText || skill.name,
                  certificate: skill.certificate || null,
                  skillCustomMetadata: mapCacheCustomMetaData(skill),
                  skillLevel: {
                    __typename: 'SkillLevel',
                    ...skill.skillLevel,
                    rating: skill.skillLevel.rating || -1
                  }
                });

                return acc;
              }, {})
            )
          }
        }
      };

      if (!isPsaPraaResourceMatchingEnabled) {
        try {
          await updateSkillsMutation(updateSkillsMutationParams);
        } catch (err) {
          setError(err);
        }
      } else {
        const initialUserDefinedSkillIds = (
          initialValues?.userDefinedSkills || []
        ).map(r => r.id);
        const addSkills = userDefinedSkills
          .filter(k => !k.id)
          .map(ud => mapAddUserDefinedSkills(ud));

        const updateSkills = userDefinedSkills
          .filter(r => r.id)
          .map(ud => mapUpdatedUserDefinedSkills(ud));

        const existingSkillIds = updateSkills.map(r => r.id);

        try {
          await Promise.all([
            updateSkillsMutation(updateSkillsMutationParams),
            applyUserDefinedSkillModificationsForUser({
              addSkills,
              updateSkills,
              removeSkills: initialUserDefinedSkillIds.filter(
                r => !existingSkillIds.includes(r)
              )
            })
          ]);
        } catch (err) {
          setError(err);
        }
      }
    },
    [
      userUri,
      isPsaPraaResourceMatchingEnabled,
      updateSkillsMutation,
      initialValues?.userDefinedSkills,
      applyUserDefinedSkillModificationsForUser
    ]
  );

  return { onSubmit, error };
};

export default useUpdateUserSkillAssignments;
